import {
  Badge,
  Box,
  Button,
  Collapse,
  Drawer,
  FormControl,
  Grid2,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { FaHome } from "react-icons/fa";
import { HiBellAlert } from "react-icons/hi2";
import { TbReportSearch, TbSettings } from "react-icons/tb";
import { GrUserAdmin } from "react-icons/gr";
import { NavLink, useNavigate } from "react-router-dom";
import { initUserData, UserRole, useSessionStorage } from "../IF/userSession";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { baseLinkStyle } from "./layout/AppLayout";
import { useAppSelector } from "Hooks/hooks";
import axios from "axios";
import { API_BASE_URL } from "Utils/apiUtil";
import { ViewContext } from "Contexts/ViewProvider";
import { IoPeople } from "react-icons/io5";
import { MdLogout } from "react-icons/md";
import { menuItem } from "IF/interfaceType";
import { DataContext } from "Contexts/DataProvider";
import { fetchData } from "Action/slice";
import { useThemeMode } from "Contexts/ThemeContext";

type SidebarProps = {
  drawerWidth: number;
  mobileOpen: boolean;
  handleDrawerTransitionEnd: () => void;
  handleDrawerClose: () => void;
  handleDrawerToggle: () => void;
};

type AppChangeLog = {
  version: string;
  releaseDate: string;
  changes: string[];
};

type AppInfo = {
  version: string;
  releaseDate: string;
  changeLogs: AppChangeLog[];
};

const Sidebar = ({
  drawerWidth,
  mobileOpen,
  handleDrawerTransitionEnd,
  handleDrawerClose,
  handleDrawerToggle,
}: SidebarProps) => {
  // セッションのuserDataを取得
  const { getUserData, setUserData } = useSessionStorage();
  const userData = getUserData();
  const { userRole, companyCode: userCompanyCode } = userData;

  // サイドバーデータ持ち回り
  const sidebarContext = useContext(DataContext);
  if (!sidebarContext) {
    throw new Error("ChildComponent must be used within an ViewProvider");
  }
  const {
    companyListData,
    setCompanyListData,
    selectedCompanyCode,
    setSelectedCompanyCode,
    menuItems,
    setMenuItems,
    choiceMenu,
    setChoiceMenu,
    toggleMenu,
    isOpen,
  } = sidebarContext;

  // レイアウト設定
  const [maxHeight, setMaxHeight] = useState(600);

  // SIMID,磁界IDスイッチ
  const context = React.useContext(ViewContext);

  if (!context) {
    throw new Error("ChildComponent must be used within an ViewProvider");
  }
  const { setVisible, setIsAlertPriorityVisible } = context;

  const theme = useTheme();
  const { mode, toggleMode } = useThemeMode();
  const activeLinkStyle = {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.2)"
        : "rgba(0, 0, 0, 0.08)",
  };

  // ホームアイコンのバッジ設定
  const [homeBadge, setHomeBadge] = useState<boolean>(false);
  // ホームテキスト
  const [homeText, setHomeText] = useState<string>("");
  const navigation = useNavigate();
  const { data } = useAppSelector((state) => state.data);


  const [appInfo, setAppInfo] = useState<AppInfo | null>(null);
  const [changeLogOpen, setChangeLogOpen] = useState(false);


  useEffect(() => {
    const getAppInfo = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/AppInfo/GetAppInfo`);

        setAppInfo({
          version: response.data.version ?? response.data.Version ?? "",
          releaseDate: response.data.releaseDate ?? response.data.ReleaseDate ?? "",
          changeLogs: response.data.changeLogs ?? response.data.ChangeLogs ?? [],
        });
      } catch (error) {
        console.error("アプリ情報の取得に失敗しました:", error);
      }
    };

    getAppInfo();
  }, []);


  // 初期表示時処理
  useEffect(() => {
    const handleResize = () => {
      const windowHeight = window.innerHeight - 290;
      setMaxHeight(windowHeight);
    };

    // 初期表示時とリサイズ時に高さを計算
    handleResize();
    window.addEventListener("resize", handleResize);

    // クリーンアップ
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // バッジの表示/非表示設定
  useEffect(() => {
    const dangerArea = data.serialNoDataList.filter((item) =>
      item.dangerNum > 0 ? item : null
    );
    if (dangerArea.length > 0) {
      setHomeBadge(true);
    } else {
      setHomeBadge(false);
    }
  }, [data]);

  // 企業コード変更時処理
  useEffect(() => {
    // 登録されているすべての企業コードを取得
    const getCompanyCode = async () => {
      try {
        // API呼び出し
        const response = await axios.get(
          `${API_BASE_URL}/Sidebar/GetAllCompanyCode`
        );
        setCompanyListData(response.data);
        //setAllCompany(response.data);
      } catch (error) {
        console.error("企業コードの取得に失敗しました:", error);
      }
    };
    if (UserRole.YKC === userRole) {
      // 全企業コードを取得
      getCompanyCode();
      if (userCompanyCode !== "") {
        setSelectedCompanyCode(userCompanyCode);
      }
    } else {
      setSelectedCompanyCode(userCompanyCode);
    }
  }, [userRole, userCompanyCode, setSelectedCompanyCode, setCompanyListData]);

  // 初回とselectedCompanyCode変更の度メニュー情報作成
  useEffect(() => {
    if (selectedCompanyCode) {
      const fetchMenuTexts = async () => {
        try {
          // API呼び出し
          const response = await axios.get(
            `${API_BASE_URL}/Sidebar/GetMenuItems`,
            {
              params: { schema: selectedCompanyCode },
            }
          );
          const newTexts: string[] = response.data;

          // 更新対象のメニュー項目のテキストを新しいテキストに置き換える
          const menuItemsWk: menuItem[] = [
            ...(UserRole.YKC === userRole
              ? [
                {
                  text: "YKC専用",
                  icon: GrUserAdmin,
                  subItems: [
                    { text: "契約企業管理", path: "/IdIssuance" },
                    {
                      text: "シリアルNo紐づけ設定",
                      path: "/SimSerialNoMapping",
                    },
                    {
                      text: "バックアップデータ管理",
                      path: "/ManageBackupsPage",
                    },
                    {
                      text: "詳細設定",
                      path: "/AdvancedConfiguration",
                    },
                  ],
                },
              ]
              : []),
            {
              text: newTexts[0],
              path: "/Dashboard",
              icon: FaHome,
            },
            {
              text: newTexts[1],
              path: "/Alert",
              icon: HiBellAlert,
            },
            {
              text: newTexts[2],
              path: "/Report",
              icon: TbReportSearch,
            },
            ...(UserRole.Admin === userRole ||
              UserRole.YKC === userRole ||
              UserRole.RENTAL === userRole
              ? [
                {
                  text: newTexts[3],
                  icon: TbSettings,
                  subItems: [
                    { text: newTexts[4], path: "/Setting/Worker" },
                    { text: newTexts[5], path: "/Setting/Area" },
                    { text: newTexts[6], path: "/Setting/AreaHistory" },
                    UserRole.YKC === userRole
                      ? {
                        text: newTexts[7],
                        subItems: [
                          { text: newTexts[7], path: "/Setting/Mail" },
                          {
                            text: "メール・SMS送信履歴",
                            path: "/Setting/MessageLogs",
                          },
                        ],
                      }
                      : { text: newTexts[7], path: "/Setting/Mail" },
                    { text: newTexts[8], path: "/Setting/User" },
                    { text: newTexts[9], path: "/Setting/Various" },
                  ],

                  // subItems: [
                  //   { text: newTexts[4], path: "/Setting/Worker" },
                  //   { text: newTexts[5], path: "/Setting/Area" },
                  //   UserRole.YKC === userRole
                  //     ? {
                  //       text: newTexts[6],
                  //       subItems: [
                  //         { text: newTexts[6], path: "/Setting/Mail" },
                  //         {
                  //           text: "メール・SMS送信履歴",
                  //           path: "/Setting/MessageLogs",
                  //         },
                  //       ],
                  //     }
                  //     : { text: newTexts[6], path: "/Setting/Mail" },
                  //   { text: newTexts[7], path: "/Setting/User" },
                  //   { text: newTexts[8], path: "/Setting/Various" },
                  // ],
                },
              ]
              : []),
          ];

          //メニューを設定
          setMenuItems(menuItemsWk);
          //ホーム画面のテキストを保存
          setHomeText(newTexts[0]);
        } catch (error) {
          console.error("メニュー項目のテキスト更新に失敗しました:", error);
        }
      };

      fetchMenuTexts();
    } else {
      //YKCユーザ初期表示
      const menuItemsWk: menuItem[] = [
        ...(UserRole.YKC === userRole
          ? [
            {
              text: "YKC専用",
              icon: GrUserAdmin,
              subItems: [
                { text: "契約企業管理", path: "/IdIssuance" },
                { text: "シリアルNo紐づけ設定", path: "/SimSerialNoMapping" },
                {
                  text: "バックアップデータ管理",
                  path: "/ManageBackupsPage",
                },
                {
                  text: "詳細設定",
                  path: "/AdvancedConfiguration",
                },
              ],
            },
          ]
          : []),
      ];
      setMenuItems(menuItemsWk);
    }
  }, [selectedCompanyCode, userRole, choiceMenu, setChoiceMenu, setMenuItems]);

  // 変更処理
  const handleCompanyCodeChange = (event: SelectChangeEvent<string>) => {
    const newCompanyCode = event.target.value as string;
    setSelectedCompanyCode(newCompanyCode);

    // 選択された会社名を取得
    const selectedCompany = companyListData.find(
      (company) => company.companyCode === newCompanyCode
    );
    if (selectedCompany?.companyName) {
      const user = {
        ...userData,
        companyCode: newCompanyCode,
        companyName: selectedCompany.companyName,
      };
      setUserData(user);
      fetchData(user);
    } else {
    }
  };

  // ログアウトボタン押下時処理
  const handleLogout = () => {
    setUserData(initUserData);
    setSelectedCompanyCode("");
    if (mode === "dark") {
      toggleMode();
    }
    navigation("/");
  };

  const loginInfo = (
    <>
      <Box sx={{ width: "100%", mb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "flex-end" }}>
          <Grid2 size={{ xs: 4 }} sx={{ margin: "0px", padding: "0px" }}>
            <ListItem sx={{ margin: "0px", padding: "0px" }}>
              <ListItemIcon
                sx={{
                  minWidth: "auto",
                  mr: 0.5,
                }}
              >
                <IoPeople style={{ fontSize: "2.0em" }} />
              </ListItemIcon>
              <ListItemText
                sx={{
                  "& .MuiTypography-root": {
                    mb: 0,
                    fontSize: "0.9rem",
                  },
                }}
                primary="ユーザー情報"
              />
            </ListItem>
          </Grid2>
          <Grid2 size={{ xs: 6 }} sx={{ ml: "auto", mb: 1 }}>
            {/* ログアウトボタンをテーブルの下に配置 */}
            <Button
              variant="contained"
              color="primary"
              onClick={handleLogout}
              sx={{
                ml: "auto",
                display: "block",
                fontSize: "0.8rem",
              }}
            >
              <ListItem sx={{ margin: "0px", padding: "0px" }}>
                <ListItemIcon
                  sx={{
                    minWidth: "auto",
                    mr: 0.5,
                  }}
                >
                  <MdLogout style={{ fontSize: "1.5em" }} />
                </ListItemIcon>
                <ListItemText
                  sx={{
                    "& .MuiTypography-root": {
                      fontSize: "0.7rem",
                    },
                  }}
                  primary="ログアウト"
                />
              </ListItem>
            </Button>
          </Grid2>
        </Box>
        <TableContainer component={Paper}>
          <Table sx={{ tableLayout: "fixed" }}>
            <TableBody>
              {/* 企業コード */}
              <TableRow>
                <TableCell
                  component="th"
                  scope="row"
                  align="center"
                  size="small"
                  sx={{
                    fontSize: "0.8rem",
                    fontWeight: "fontWeightRegular",
                    backgroundColor: theme.palette.custom.tableHeader,
                    color: "white",
                    width: "40%",
                    p: 1,
                  }}
                >
                  企業コード
                </TableCell>
                <Tooltip
                  title={userData?.displayCompanyCode}
                  placement="right"
                  arrow
                >
                  <TableCell
                    sx={{
                      fontSize: "0.8rem",
                      fontWeight: "fontWeightRegular",
                      backgroundColor: "background.default",
                      width: "60%",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    size="small"
                  >
                    {userData?.displayCompanyCode}
                  </TableCell>
                </Tooltip>
              </TableRow>

              {/* 企業名 */}
              <TableRow>
                <TableCell
                  component="th"
                  scope="row"
                  align="center"
                  size="small"
                  sx={{
                    fontSize: "0.8rem",
                    fontWeight: "fontWeightRegular",
                    backgroundColor: theme.palette.custom.tableHeader,
                    color: "white",
                    width: "40%",
                    p: 1,
                  }}
                >
                  企業名
                </TableCell>
                <Tooltip
                  title={userData?.displayCompanyName}
                  placement="right"
                  arrow
                >
                  <TableCell
                    size="small"
                    sx={{
                      fontSize: "0.8rem",
                      fontWeight: "fontWeightRegular",
                      width: "60%",
                      backgroundColor: "background.default",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {userData?.displayCompanyName}
                  </TableCell>
                </Tooltip>
              </TableRow>

              {/* ログインID */}
              <TableRow>
                <TableCell
                  size="small"
                  component="th"
                  scope="row"
                  align="center"
                  sx={{
                    fontSize: "0.8rem",
                    fontWeight: "fontWeightRegular",
                    backgroundColor: theme.palette.custom.tableHeader,
                    color: "white",
                    width: "40%",
                    p: 1,
                  }}
                >
                  ログインID
                </TableCell>
                <Tooltip title={userData?.id} placement="right" arrow>
                  <TableCell
                    size="small"
                    sx={{
                      fontSize: "0.8rem",
                      fontWeight: "fontWeightRegular",
                      backgroundColor: "background.default",
                      width: "60%",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {userData?.id}
                  </TableCell>
                </Tooltip>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </>
  );

  // メニュー作成
  const renderMenuItems = (
    items: any[],
    depth: number = 0
  ): React.ReactNode[] => {
    return items.map((item) => {
      const hasSubItems = item.subItems && item.subItems.length > 0;
      const padding = 2 + depth * 2;

      if (hasSubItems) {
        return (
          <React.Fragment key={item.text}>
            <ListItemButton
              onClick={() => toggleMenu(item.text, true)}
              sx={{
                maxHeight: 40,
                px: 2,
                pl: padding,
              }}
            >
              {depth === 0 && item.icon && (
                <ListItemIcon sx={{ fontSize: "25px" }}>
                  <item.icon />
                </ListItemIcon>
              )}
              <ListItemText primary={item.text} />
              {isOpen(item.text) ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={isOpen(item.text)} timeout="auto" unmountOnExit>
              <List disablePadding>
                {renderMenuItems(item.subItems, depth + 1)}
              </List>
            </Collapse>
          </React.Fragment>
        );
      } else {
        return (
          <NavLink
            key={item.text}
            to={item.path}
            style={({ isActive }) => ({
              ...baseLinkStyle,
              ...(isActive ? activeLinkStyle : {}),
            })}
            onClick={() => {
              setChoiceMenu(item.text);
              setVisible(item.path === "/Dashboard" || item.path === "/Alert");
              setIsAlertPriorityVisible(item.path === "/Dashboard");
            }}
          >
            <ListItemButton
              sx={{
                maxHeight: 40,
                px: 2,
                pl: padding,
              }}
            >
              {depth === 0 && item.icon && (
                <ListItemIcon sx={{ fontSize: "28px" }}>
                  {item.text === homeText ? (
                    <Badge
                      color="error"
                      variant="dot"
                      invisible={!homeBadge}
                      sx={{
                        "& .MuiBadge-dot": {
                          width: "12px",
                          height: "12px",
                          borderRadius: "6px",
                        },
                      }}
                    >
                      <item.icon />
                    </Badge>
                  ) : (
                    <item.icon />
                  )}
                </ListItemIcon>
              )}
              <ListItemText primary={item.text} />
            </ListItemButton>
          </NavLink>
        );
      }
    });
  };

  const drawer = (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          fontSize: "10px",
          justifyContent: "flex-start",
          alignItems: "flex-start",
          height: "auto",
          padding: "10px 5px 5px 5px",
          gap: 1,
        }}
      >
        {loginInfo}
        {UserRole.YKC === userRole && (
          <Tooltip
            key={userData?.companyCode}
            title={`会社コード: ${userData?.companyCode}\n会社名: ${userData?.companyName}`}
            arrow
            placement="top"
          >
            <FormControl fullWidth>
              <InputLabel id="company-code-label">企業選択</InputLabel>
              <Select
                labelId="company-code-label"
                id="company-code-select"
                value={selectedCompanyCode}
                label="企業選択"
                onChange={handleCompanyCodeChange}
              >
                {companyListData.map((data) => (
                  <MenuItem key={data.companyCode} value={data.companyCode}>
                    {data.companyCode}-{data.companyName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Tooltip>
        )}
      </Box>
      {/* <Divider /> */}
      <List sx={{ maxHeight: maxHeight, overflowY: "auto" }}>
        {renderMenuItems(menuItems)}
      </List>
    </>
  );

  const canOpenChangeLog = UserRole.YKC === userRole;

  // const logo = (
  //   <Box
  //     sx={{
  //       display: "flex",
  //       justifyContent: "center",
  //       marginTop: "auto",
  //       paddingRight: "12px",
  //       paddingBottom: "10px",
  //     }}
  //   >
  //     <NavLink
  //       to={UserRole.YKC === userRole ? "/IdIssuance" : "/Dashboard"}
  //       onClick={() => {
  //         if (UserRole.YKC === userRole) {
  //           setChoiceMenu("契約企業管理");
  //         } else {
  //           setChoiceMenu(menuItems[0].text);
  //         }
  //       }}
  //     >
  //       {mode === "dark" ? (
  //         <img
  //           src="./images/harness_alert_icon_dark.png"
  //           className="icon"
  //           alt="logo"
  //         />
  //       ) : (
  //         <img
  //           src="./images/harness_alert_icon.png"
  //           className="icon"
  //           alt="logo"
  //         />
  //       )}
  //     </NavLink>
  //   </Box>
  // );

  const logo = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        marginTop: "auto",
        paddingRight: "12px",
        paddingBottom: "10px",
      }}
    >
      <NavLink
        to={UserRole.YKC === userRole ? "/IdIssuance" : "/Dashboard"}
        onClick={() => {
          if (UserRole.YKC === userRole) {
            setChoiceMenu("契約企業管理");
          } else {
            setChoiceMenu(menuItems[0].text);
          }
        }}
      >
        {mode === "dark" ? (
          <img
            src="./images/harness_alert_icon_dark.png"
            className="icon"
            alt="logo"
          />
        ) : (
          <img
            src="./images/harness_alert_icon.png"
            className="icon"
            alt="logo"
          />
        )}
      </NavLink>

      {appInfo?.version && (
        <Typography
          onClick={() => {
            if (canOpenChangeLog) {
              setChangeLogOpen(true);
            }
          }}
          sx={{
            fontSize: "12px",
            color: mode === "dark" ? "#ffffff" : "#5a2a2a",
            mt: 0.5,
            cursor: canOpenChangeLog ? "pointer" : "default",
            textDecoration: canOpenChangeLog ? "underline" : "none",
          }}
        >
          Ver. {appInfo.version}
        </Typography>
      )}
    </Box>
  );

  return (
    <>
      <Box
        component="nav"
        sx={{
          display: "flex",
          width: { md: drawerWidth },
          flexShrink: { md: 0 },
        }}
      >
        {/* モバイル用 */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onTransitionEnd={handleDrawerTransitionEnd}
          onClose={handleDrawerClose}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              backgroundColor: theme.palette.custom.sidebar,
            },
          }}
        >
          {drawer}
          {logo}
        </Drawer>

        {/* PC用 */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              backgroundColor: theme.palette.custom.sidebar,
            },
          }}
          open
        >
          {drawer}
          {logo}
        </Drawer>
      </Box>

      <Dialog
        open={changeLogOpen}
        onClose={() => setChangeLogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>改修履歴</DialogTitle>

        <DialogContent dividers>
          {appInfo?.changeLogs?.length ? (
            appInfo.changeLogs.map((log) => (
              <Box key={log.version} sx={{ mb: 2 }}>
                <Typography sx={{ fontWeight: "bold", mb: 0.5 }}>
                  Ver. {log.version}　{log.releaseDate}
                </Typography>

                {log.changes.map((change, index) => (
                  <Typography key={index} sx={{ fontSize: "14px", ml: 1 }}>
                    ・{change}
                  </Typography>
                ))}
              </Box>
            ))
          ) : (
            <Typography sx={{ fontSize: "14px" }}>
              改修履歴はありません。
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </>
  );

};

export default Sidebar;
